import { gql } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import {
  executeGraphqlRequest,
  readCachedGraphqlQuery,
} from '../apollo/graphql-request';

export interface EventSummary {
  city?: {
    placeId?: string | null;
    label: string;
    coordinates?: {
      lat: number;
      lng: number;
    } | null;
  } | null;
  uuid: string;
  accountUuid: string;
  title: string;
  description?: string | null;
  location?: string | null;
  startsAt: string;
  endsAt: string;
  createdAt: string;
  updatedAt: string;
  account: {
    uuid: string;
    username: string;
  };
}

export interface CreateEventInput {
  accountUuid?: string;
  title: string;
  description?: string;
  city?: {
    placeId?: string | null;
    label: string;
    coordinates?: {
      lat: number;
      lng: number;
    } | null;
  } | null;
  location?: string | null;
  startsAt: string;
  endsAt: string;
}

export interface UpdateEventInput {
  uuid: string;
  title: string;
  description?: string;
  city?: {
    placeId?: string | null;
    label: string;
    coordinates?: {
      lat: number;
      lng: number;
    } | null;
  } | null;
  location?: string | null;
  startsAt: string;
  endsAt: string;
}

const EVENT_SUMMARY_SELECTION_SET = `
  uuid
  accountUuid
  title
  description
  location
  startsAt
  endsAt
  createdAt
  updatedAt
  account {
    uuid
    username
  }
`;

const EVENTS_QUERY = `
  query Events {
    events {
      ${EVENT_SUMMARY_SELECTION_SET}
    }
  }
`;

const ACCOUNT_EVENTS_QUERY = `
  query AccountEvents($accountUuid: ID!) {
    accountEvents(accountUuid: $accountUuid) {
      ${EVENT_SUMMARY_SELECTION_SET}
    }
  }
`;

const EVENT_QUERY = `
  query Event($uuid: ID!) {
    event(uuid: $uuid) {
      ${EVENT_SUMMARY_SELECTION_SET}
    }
  }
`;

const CREATE_EVENT_MUTATION = `
  mutation CreateEvent($input: CreateEventInput!) {
    createEvent(input: $input) {
      ${EVENT_SUMMARY_SELECTION_SET}
    }
  }
`;

const UPDATE_EVENT_MUTATION = `
  mutation UpdateEvent($input: UpdateEventInput!) {
    updateEvent(input: $input) {
      ${EVENT_SUMMARY_SELECTION_SET}
    }
  }
`;

const EVENTS_QUERY_DOCUMENT = gql(EVENTS_QUERY);
const ACCOUNT_EVENTS_QUERY_DOCUMENT = gql(ACCOUNT_EVENTS_QUERY);
const EVENT_QUERY_DOCUMENT = gql(EVENT_QUERY);

async function executeEventRequest<TData>(
  query: string,
  variables: Record<string, unknown>,
  accessToken?: string
): Promise<TData> {
  return executeGraphqlRequest<TData>(query, variables, {
    accessToken,
    requestName: 'Event',
  });
}

function normalizeEventSummary(event: EventSummary): EventSummary {
  return event;
}

function normalizeEventSummaries(events: EventSummary[]): EventSummary[] {
  return events.map(normalizeEventSummary);
}

function normalizeEventCityInput(
  city?: CreateEventInput['city'] | UpdateEventInput['city']
) {
  if (!city) {
    return city;
  }

  // The API takes the city as an EventCityInput object, never a bare label.
  const label = city.label?.trim();
  return label
    ? { label, placeId: city.placeId ?? null, coordinates: city.coordinates ?? null }
    : null;
}

function upsertEventInList(events: EventSummary[], nextEvent: EventSummary): EventSummary[] {
  const existingEventIndex = events.findIndex((event) => event.uuid === nextEvent.uuid);

  if (existingEventIndex < 0) {
    return [nextEvent, ...events];
  }

  const nextEvents = [...events];
  nextEvents[existingEventIndex] = nextEvent;
  return nextEvents;
}

function updateCachedEventQueries(nextEvent: EventSummary) {
  apolloClient.cache.updateQuery<{ event: EventSummary | null }>(
    {
      query: EVENT_QUERY_DOCUMENT,
      variables: { uuid: nextEvent.uuid },
    },
    () => ({ event: nextEvent })
  );

  apolloClient.cache.updateQuery<{ events: EventSummary[] }>(
    {
      query: EVENTS_QUERY_DOCUMENT,
    },
    (cachedData) => {
      if (!cachedData?.events) {
        return cachedData;
      }

      return {
        events: upsertEventInList(cachedData.events, nextEvent),
      };
    }
  );

  apolloClient.cache.updateQuery<{ accountEvents: EventSummary[] }>(
    {
      query: ACCOUNT_EVENTS_QUERY_DOCUMENT,
      variables: { accountUuid: nextEvent.accountUuid },
    },
    (cachedData) => {
      if (!cachedData?.accountEvents) {
        return cachedData;
      }

      return {
        accountEvents: upsertEventInList(cachedData.accountEvents, nextEvent),
      };
    }
  );
}

export async function fetchEvents(accessToken?: string): Promise<EventSummary[]> {
  const response = await executeEventRequest<{ events: EventSummary[] }>(
    EVENTS_QUERY,
    {},
    accessToken
  );

  return normalizeEventSummaries(response.events);
}

export function readCachedEvents(): EventSummary[] | undefined {
  const cachedData = readCachedGraphqlQuery<{ events: EventSummary[] }>(EVENTS_QUERY);

  return cachedData ? normalizeEventSummaries(cachedData.events) : undefined;
}

export async function fetchAccountEvents(
  accountUuid: string,
  accessToken?: string
): Promise<EventSummary[]> {
  const response = await executeEventRequest<{ accountEvents: EventSummary[] }>(
    ACCOUNT_EVENTS_QUERY,
    { accountUuid },
    accessToken
  );

  return normalizeEventSummaries(response.accountEvents);
}

export function readCachedAccountEvents(accountUuid: string): EventSummary[] | undefined {
  const cachedData = readCachedGraphqlQuery<{ accountEvents: EventSummary[] }>(
    ACCOUNT_EVENTS_QUERY,
    { accountUuid }
  );

  return cachedData ? normalizeEventSummaries(cachedData.accountEvents) : undefined;
}

export async function fetchEventByUuid(
  uuid: string,
  accessToken?: string
): Promise<EventSummary | null> {
  const response = await executeEventRequest<{ event: EventSummary | null }>(
    EVENT_QUERY,
    { uuid },
    accessToken
  );

  return response.event ? normalizeEventSummary(response.event) : null;
}

export function readCachedEventByUuid(uuid: string): EventSummary | null | undefined {
  const cachedData = readCachedGraphqlQuery<{ event: EventSummary | null }>(EVENT_QUERY, {
    uuid,
  });

  if (!cachedData) {
    return undefined;
  }

  return cachedData.event ? normalizeEventSummary(cachedData.event) : null;
}

// The API makes the Event for the signed-in Account and has no accountUuid in CreateEventInput
// (it refuses the whole request when one is sent), so it stays on this side.
export function buildCreateEventVariables(input: CreateEventInput) {
  const { accountUuid: _accountUuid, ...rest } = input;
  return { input: { ...rest, city: normalizeEventCityInput(input.city) } };
}

export function buildUpdateEventVariables(input: UpdateEventInput) {
  return { input: { ...input, city: normalizeEventCityInput(input.city) } };
}

export async function createEventRequest(
  input: CreateEventInput,
  accessToken?: string
): Promise<EventSummary> {
  const response = await executeEventRequest<{ createEvent: EventSummary }>(
    CREATE_EVENT_MUTATION,
    buildCreateEventVariables(input),
    accessToken
  );

  const createdEvent = normalizeEventSummary(response.createEvent);
  updateCachedEventQueries(createdEvent);
  return createdEvent;
}

export async function updateEventRequest(
  input: UpdateEventInput,
  accessToken?: string
): Promise<EventSummary> {
  const response = await executeEventRequest<{ updateEvent: EventSummary }>(
    UPDATE_EVENT_MUTATION,
    buildUpdateEventVariables(input),
    accessToken
  );

  const updatedEvent = normalizeEventSummary(response.updateEvent);
  updateCachedEventQueries(updatedEvent);
  return updatedEvent;
}
